import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import {
  Line, Area, ComposedChart, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { AXIS } from './chartTheme';
import { tipFormatter, AreaGradient } from './ChartKit';

/**
 * Seccion Caja rehecha con etiquetas honestas: cobrado segun lo cargado,
 * egresos (compras + operativos), curva doble, medios de pago con costo
 * de recargo, y cierres de caja (teorico vs contado).
 */

const money = (n: number) => {
  if (n == null) return '—';
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}$${Math.round(a / 1e3)}k`;
  return `${sign}$${Math.round(a).toLocaleString('es-AR')}`;
};

function DeltaChip({ value, invert = false }: { value: number | null; invert?: boolean }) {
  if (value == null) return <span className="text-xs text-muted-foreground">sin ref.</span>;
  const good = invert ? value < 0 : value > 0;
  const color = value === 0 ? 'text-muted-foreground bg-muted' : good ? 'text-green-600 bg-green-500/10' : 'text-red-600 bg-red-500/10';
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded ${color}`}>
      {value > 0 ? '▲' : value < 0 ? '▼' : ''} {value > 0 ? '+' : ''}{value}% vs per. ant.
    </span>
  );
}

export default function CajaSection({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [show, setShow] = useState({ cobrado: true, egresos: true });

  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    setError('');
    setLoading(true);
    axios.get('/api/reports/tablero/caja', { params: { startDate, endDate } })
      .then((r) => setD(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Error cargando caja'))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading && !d) return <div className="py-10 text-center text-muted-foreground">Cargando caja...</div>;
  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!d) return null;

  const maxMp = Math.max(1, ...d.mediosPago.map((m: any) => m.monto));
  const totalRecargo = d.mediosPago.reduce((a: number, m: any) => a + m.recargoIncluido, 0);

  return (
    <div className={`space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Cobrado (según lo cargado)</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.kpis.cobrado.value)}</p>
          <DeltaChip value={d.kpis.cobrado.deltaPct} />
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Egresos (compras + operativos)</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.kpis.egresos.value)}</p>
          <DeltaChip value={d.kpis.egresos.deltaPct} invert />
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Resultado del período</p>
          <p className={`text-2xl font-semibold leading-none mb-1.5 ${d.kpis.resultado.value < 0 ? 'text-red-600' : ''}`}>{money(d.kpis.resultado.value)}</p>
          <DeltaChip value={d.kpis.resultado.deltaPct} />
        </CardContent></Card>
      </div>

      {!d.egresosOperativosCargados && (
        <p className="text-xs text-amber-600 px-1">
          ⚠ Los egresos operativos (sueldos, servicios, impuestos) todavía no se cargaron — el KPI de egresos solo refleja compras a proveedores. Cargalos desde <a href="/operating-expenses" className="underline font-medium">Finanzas → Gastos operativos</a> y van a aparecer acá y en el tablero Resumen automáticamente.
        </p>
      )}

      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-sm">Cobrado vs egresos</h3>
            <div className="flex gap-3 text-xs">
              {[{ k: 'cobrado', label: 'Cobrado', color: 'hsl(var(--chart-2))' }, { k: 'egresos', label: 'Egresos', color: 'hsl(var(--chart-5))' }].map((s) => (
                <button key={s.k} onClick={() => setShow((p) => ({ ...p, [s.k]: !(p as any)[s.k] }))} className={`flex items-center gap-1.5 ${!(show as any)[s.k] ? 'opacity-35' : ''}`}>
                  <span className="inline-block w-3 h-0.5 rounded" style={{ background: s.color }} />{s.label}
                </button>
              ))}
            </div>
          </div>
          <ChartContainer
            config={{
              cobrado: { label: 'Cobrado', color: 'hsl(var(--chart-2))' },
              egresos: { label: 'Egresos', color: 'hsl(var(--chart-5))' }
            }}
            className="h-[240px] w-full"
          >
            <ComposedChart data={d.serie} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <defs>
                <AreaGradient id="fillCobrado" color="var(--color-cobrado)" />
              </defs>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
              <XAxis dataKey="label" {...AXIS} />
              <YAxis {...AXIS} tickFormatter={(v: number) => money(v)} width={56} />
              <ChartTooltip content={<ChartTooltipContent formatter={tipFormatter({ cobrado: 'Cobrado', egresos: 'Egresos' }, money)} />} />
              {show.egresos && <Line type="monotone" dataKey="egresos" stroke="var(--color-egresos)" strokeWidth={1.8} dot={false} />}
              {show.cobrado && <Area type="monotone" dataKey="cobrado" stroke="var(--color-cobrado)" strokeWidth={2.2} fill="url(#fillCobrado)" dot={{ r: 2 }} />}
            </ComposedChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-semibold text-sm">Medios de pago</h3>
              {totalRecargo > 0 && <span className="text-xs text-muted-foreground">recargos incluidos: {money(totalRecargo)}</span>}
            </div>
            {d.mediosPago.length === 0 && <p className="text-sm text-muted-foreground">Sin cobros en el período</p>}
            <div className="space-y-2.5">
              {d.mediosPago.map((m: any) => (
                <div key={m.code}>
                  <div className="flex items-baseline justify-between text-sm mb-1">
                    <span className="font-medium">{m.name} <span className="text-xs text-muted-foreground font-normal">({m.ventas})</span></span>
                    <span>
                      {money(m.monto)} <span className="text-xs text-muted-foreground">{m.pct}%</span>
                      {m.recargoIncluido > 0 && <span className="text-xs text-amber-600 ml-1.5">rec. {money(m.recargoIncluido)}</span>}
                    </span>
                  </div>
                  <div className="w-full h-2 rounded bg-muted overflow-hidden">
                    <div className="h-full rounded bg-primary/70" style={{ width: `${(m.monto / maxMp) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Cierres de caja (teórico vs contado)</h3>
            {d.cierres.length === 0 && <p className="text-sm text-muted-foreground">Sin cierres registrados. Se hacen desde Finanzas → Caja.</p>}
            {d.cierres.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground text-left">
                    <th className="pb-1.5 font-normal">Fecha</th>
                    <th className="pb-1.5 font-normal text-right">Teórico</th>
                    <th className="pb-1.5 font-normal text-right">Contado</th>
                    <th className="pb-1.5 font-normal text-right">Diferencia</th>
                  </tr>
                </thead>
                <tbody>
                  {d.cierres.map((c: any) => (
                    <tr key={c.id} className="border-t">
                      <td className="py-1.5 pr-2">{new Date(c.fecha + 'T12:00:00').toLocaleDateString('es-AR')}</td>
                      <td className="py-1.5 pr-2 text-right text-muted-foreground">{money(c.teorico)}</td>
                      <td className="py-1.5 pr-2 text-right">{money(c.contado)}</td>
                      <td className={`py-1.5 text-right font-medium ${c.diferencia < 0 ? 'text-red-600' : c.diferencia > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        {c.diferencia === 0 ? '✓ exacta' : money(c.diferencia)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
